import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send, Calendar } from "lucide-react";
import { countries } from "../../Contact/ContactUs";
import { ContectUsQuerry, fetIpAddress } from "../../../utils/Api/api";
import useGeoLocation from "../../../components/geoLocation/GeoLoaction";
interface DemoRequestModalProps {
  open: boolean;
  onClose: () => void;
}
export const getCountryCodeFromPhone = async (
  phone: string,
): Promise<string | null> => {
  if (!phone) return null;

  const cleanPhone = phone.replace(/\s|-/g, "");

  let matchedCode: string | null = null;
  let maxLength = 0;

  for (const country of countries) {
    const dial = country.dialCode.replace(/-/g, "");

    if (cleanPhone.startsWith(dial) && dial.length > maxLength) {
      matchedCode = country.code;
      maxLength = dial.length;
    }
  }

  return matchedCode;
};
export const DemoRequestModal = ({ open, onClose }: DemoRequestModalProps) => {
  const [message, setMessage] = useState(
    "Dear TICCA Team,\n\nI am very impressed by your vision for work verification and would love to explore how TICCA can transform our organization's workflow. I am keen to see a live product demo and learn more about how the platform can drive accountability, transparency, and efficiency for our team.\n\nLooking forward to connecting with you.\n\nBest regards",
  );
  const [sent, setSent] = useState(false);
  const location = useGeoLocation();
  const fetchIpAddress = async () => {
    try {
      const { data } = await fetIpAddress();
      return data;
    } catch (error) {
      return "null";
    }
  };
  const handleSend = async (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent("Product Demo Request – TICCA");
    const body = encodeURIComponent(message);
    const userData = JSON.parse(sessionStorage.getItem("userData") || "{}");
    try {
      const payload = {
        ip_address: await fetchIpAddress(),
        latitude: location?.coordinates?.lat ?? null,
        longitude: location?.coordinates?.lng ?? null,
        name: userData.fname,
        email: userData.email,
        phone: userData.phone,
        country: await getCountryCodeFromPhone(userData.phone),
        subject: "Product Demo Request – TICCA",
        message: message,
        reply_via: "email",
      };
      setSent(true);
      const { data } = await ContectUsQuerry(payload);
      if (data?.status === 200) {
        setSent(false);
        setMessage(
          "Dear TICCA Team,\n\nI am very impressed by your vision for work verification and would love to explore how TICCA can transform our organization's workflow. I am keen to see a live product demo and learn more about how the platform can drive accountability, transparency, and efficiency for our team.\n\nLooking forward to connecting with you.\n\nBest regards",
        );
        onClose();
      }
    } catch (error) {
      console.error("Error sending demo request:", error);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          onClick={onClose}
        >
          <div className="absolute inset-0 bg-foreground/20 backdrop-blur-sm" />
          <motion.div
            initial={{ opacity: 0, scale: 0.92, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.92, y: 20 }}
            transition={{ type: "spring", damping: 25, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-background rounded-2xl border border-border shadow-brand overflow-hidden"
          >
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1.5 rounded-lg hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors z-10"
            >
              <X className="w-4 h-4" />
            </button>

            <div className="p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-ticca-blue/10 flex items-center justify-center">
                  <Calendar className="w-6 h-6 text-ticca-blue" />
                </div>
                <div>
                  <h3 className="font-display font-bold text-xl text-foreground">
                    Request a Demo
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    See TICCA in action
                  </p>
                </div>
              </div>

              {sent ? (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-10 text-center"
                >
                  <div className="w-16 h-16 rounded-full bg-ticca-green/10 flex items-center justify-center mx-auto mb-4">
                    <Send className="w-7 h-7 text-ticca-green" />
                  </div>
                  <h4 className="font-display font-bold text-lg text-foreground mb-1">
                    Demo Request Sent!
                  </h4>
                  <p className="text-sm text-muted-foreground">
                    Our team will get back to you shortly.
                  </p>
                </motion.div>
              ) : (
                <form onSubmit={handleSend}>
                  <div className="mb-6">
                    <label className="text-sm font-medium text-foreground mb-2 block">
                      Message
                    </label>
                    <textarea
                      rows={10}
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      className="w-full px-4 py-3 rounded-xl border border-border bg-secondary/30 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ticca-blue/30 focus:border-ticca-blue/50 transition-all resize-none"
                    />
                  </div>

                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full flex items-center justify-center gap-2 px-5 py-3.5 rounded-xl bg-ticca-blue text-white font-semibold text-sm shadow-brand hover:bg-ticca-blue/90 transition-colors"
                  >
                    <Send className="w-4 h-4" />
                    Send Demo Request
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
